/**
 * Boleta photo intake contract shared by the frontend and the backend.
 *
 * Single source of truth for the image formats an operator may upload and for
 * the limits the backend normalizer enforces:
 * - the frontend consumes `BOLETA_IMAGE_ACCEPT` for the file pickers and
 *   `BOLETA_MAX_INPUT_BYTES` for its early size guard;
 * - the backend consumes every constant (multer cap, normalizer limits).
 *
 * The backend decides the real format by sniffing the file content, never by
 * trusting `file.type`/`mimetype` (browsers report HEIC inconsistently).
 */

/** Accepted input MIME types; `accept` hints only, content sniffing is authoritative. */
export const BOLETA_ACCEPTED_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/heic',
  'image/heif',
  'image/avif',
] as const;

export type BoletaAcceptedImageMime = (typeof BOLETA_ACCEPTED_IMAGE_MIMES)[number];

/** File extensions for pickers that ignore MIME types (e.g. iOS HEIC). */
export const BOLETA_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.bmp',
  '.tif',
  '.tiff',
  '.heic',
  '.heif',
  '.avif',
] as const;

/** Comma-separated `accept` attribute value: MIME types plus file extensions. */
export const BOLETA_IMAGE_ACCEPT = [
  ...BOLETA_ACCEPTED_IMAGE_MIMES,
  ...BOLETA_IMAGE_EXTENSIONS,
].join(',');

/** Hard cap for the uploaded original (multer + service), in bytes. */
export const BOLETA_MAX_INPUT_BYTES = 25 * 1024 * 1024;

/** Normalized artifact contract: one lightweight standard tesseract can read. */
export const BOLETA_NORMALIZED_MIME = 'image/jpeg';
export const BOLETA_NORMALIZED_MAX_EDGE = 2400;
export const BOLETA_NORMALIZED_JPEG_QUALITY = 82;

/** Decompression-bomb guard: maximum decoded pixels of the original image. */
export const BOLETA_MAX_INPUT_PIXELS = 50_000_000;
